<template>
	<v-dialog v-model="dialogOpen" max-width="1200px" persistent scrollable>
		<v-card>
			<v-card-title class="d-flex justify-space-between align-center modal-header-primary">
				<span class="text-h6 modal-header-primary-text">Aperçu du document</span>
				<v-btn icon variant="text" @click="closeDialog" class="modal-header-primary-icon">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-card-title>
			<v-card-text>
				<div
					ref="previewContainer"
					class="preview-container"
					:class="previewLayoutClass"
				>
					<div v-if="previewPreparing" class="preview-loading">
						<v-progress-circular indeterminate color="primary" size="48" width="3" class="mb-3" />
						<p class="text-body-2 text-medium-emphasis mb-0">Préparation de l’aperçu…</p>
					</div>

					<component
						v-if="dialogOpen && deliberation && documentComponent"
						:is="documentComponent"
						:key="previewKey"
						ref="documentComponentRef"
						v-bind="documentComponentProps"
					/>

					<div v-else-if="!deliberation" class="text-center py-8 text-grey">
						Aucune délibération sélectionnée
					</div>
				</div>
			</v-card-text>
			<v-card-actions class="d-flex justify-center">
				<v-btn variant="outlined" color="secondary" @click="closeDialog">Fermer</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, type Component } from 'vue'
import type { Deliberation } from '@/types/deliberation'
import type { TableauDeliberationExportFilter } from '@/types/documentExport'
import ResultatsSessionDeliberation from './templates/ResultatsSessionDeliberation.vue'
import FeuilleRattrapage from './templates/FeuilleRattrapage.vue'
import TableauDeliberationDocument from './templates/TableauDeliberationDocument.vue'
import {
	waitForDeliberationDocumentReady,
	applyDeliberationDocumentPreviewPagination,
} from '@/composables/useDeliberationPDF'

const props = defineProps<{
	modelValue: boolean
	documentType: string | null
	deliberation: Deliberation | null
	tableauExportFilter?: TableauDeliberationExportFilter
}>()

const emit = defineEmits<{
	'update:modelValue': [value: boolean]
}>()

const dialogOpen = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value)
})

const previewContainer = ref<HTMLElement | null>(null)
const documentComponentRef = ref<{
	isReady?: boolean | { value: boolean }
	documentRef?: { value?: HTMLElement | null } | HTMLElement | null
} | null>(null)
const previewKey = ref(0)
const previewPreparing = ref(false)

const documentComponent = computed((): Component | null => {
	switch (props.documentType) {
		case 'resultats':
			return ResultatsSessionDeliberation
		case 'rattrapage':
			return FeuilleRattrapage
		case 'tableau':
			return TableauDeliberationDocument
		default:
			return null
	}
})

const previewLayoutClass = computed(() => {
	if (props.documentType === 'resultats') return 'preview-portrait'
	if (props.documentType === 'rattrapage' || props.documentType === 'tableau') return 'preview-landscape'
	return ''
})

const documentComponentProps = computed(() => {
	if (!props.deliberation) return {}
	const base = { deliberation: props.deliberation }
	if (props.documentType === 'tableau' && props.tableauExportFilter) {
		return { ...base, exportFilter: props.tableauExportFilter }
	}
	return base
})

function closeDialog() {
	dialogOpen.value = false
}

async function preparePreview() {
	if (!props.modelValue || !props.documentType || !props.deliberation) {
		previewPreparing.value = false
		return
	}

	previewPreparing.value = true
	previewKey.value += 1

	await nextTick()
	await nextTick()

	try {
		await waitForDeliberationDocumentReady(documentComponentRef.value)
		await nextTick()
		await new Promise((resolve) => setTimeout(resolve, 150))

		if (previewContainer.value) {
			applyDeliberationDocumentPreviewPagination(
				previewContainer.value,
				props.documentType
			)
		}
	} catch (error) {
		console.error('Erreur préparation aperçu document:', error)
	} finally {
		previewPreparing.value = false
	}
}

watch(
	() => [
		props.modelValue,
		props.documentType,
		props.deliberation?.id,
		props.tableauExportFilter?.includePresident,
		props.tableauExportFilter?.selectedPersonnelIds?.join(','),
	] as const,
	([open]) => {
		if (open) {
			preparePreview()
		} else {
			previewPreparing.value = false
		}
	}
)
</script>

<style scoped>
.modal-header-primary {
	background-color: #417985 !important;
	color: #fff !important;
	border-radius: 3px 3px 0 0;
}
.modal-header-primary-text {
	color: #fff !important;
}
.modal-header-primary-icon .v-icon {
	color: #fff !important;
}

.preview-container {
	position: relative;
	background: #eceff1;
	padding: 16px;
	max-height: 75vh;
	min-height: 280px;
	overflow: auto;
}

.preview-loading {
	position: absolute;
	inset: 0;
	z-index: 2;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.92);
}

/* Pages empilées comme dans le PDF */
.preview-container :deep(.paginated-wrapper),
.preview-container :deep(.paginated-wrapper-landscape),
.preview-container :deep(.paginated-wrapper-landscape-tableau) {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 20px;
	width: 100%;
}

.preview-container :deep(.page),
.preview-container :deep(.rattrapage-page-frame),
.preview-container :deep(.tableau-delib-root),
.preview-container :deep(.paginated-wrapper > *),
.preview-container :deep(.paginated-wrapper-landscape > *),
.preview-container :deep(.paginated-wrapper-landscape-tableau > *) {
	background: #fff;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
	box-sizing: border-box;
	flex-shrink: 0;
}

.preview-portrait :deep(.document-container) {
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 20px;
}

.preview-portrait :deep(.page) {
	width: min(210mm, 100%);
	max-width: 100%;
}

.preview-landscape :deep(.document-container) {
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 20px;
}

.preview-landscape :deep(.rattrapage-page-frame),
.preview-landscape :deep(.tableau-delib-root),
.preview-landscape :deep(.paginated-wrapper-landscape > *),
.preview-landscape :deep(.paginated-wrapper-landscape-tableau > *) {
	width: min(297mm, 100%);
	max-width: 100%;
}
</style>
